import type { OptimizationModel } from '@/shared/generated'

import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { RotateCcwIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import { Spinner } from '@/shared/components/ui/spinner'
import { OptimizationStatus } from '@/shared/generated'

import { useResetOptimizationMutation } from '../api/mutations'
import { useCheckOptimizationForDeleteExport } from '../lib/useCheckOptimizationForDeleteExport'

export const RevokeOptimizationAction = ({
	status,
	optimizationGroupId,
}: {
	status?: OptimizationStatus
	optimizationGroupId: string
}) => {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const checkExport = useCheckOptimizationForDeleteExport(optimizationGroupId)
	const resetOptimization = useResetOptimizationMutation({
		onSuccess: (optimization) => {
			queryClient.setQueryData(
				['optimizations', optimization.optimization_group_id],
				optimization
			)
			queryClient.setQueryData<OptimizationModel[]>(
				['optimizations'],
				(optimizations) => [
					...(optimizations || []).filter(
						(o) =>
							o.optimization_group_id !== optimization.optimization_group_id
					),
					optimization,
				]
			)
		},
	})
	if (
		status &&
		[
			OptimizationStatus.Finished,
			OptimizationStatus.Failed,
			OptimizationStatus.UpdatingFailed,
		].includes(status)
	) {
		return (
			<Button
				data-testid='revoke-optimization-button'
				size='sm'
				disabled={resetOptimization.isPending}
				onClick={() => resetOptimization.mutate(optimizationGroupId)}
			>
				{resetOptimization.isPending ? <Spinner /> : <RotateCcwIcon />}
				{t('general_revoke', { defaultValue: 'Revoke' })}
			</Button>
		)
	}
	if (status === OptimizationStatus.Locked) {
		return (
			<Button
				data-testid='revoke-optimization-button'
				size='sm'
				disabled={resetOptimization.isPending}
				onClick={checkExport(() =>
					resetOptimization.mutate(optimizationGroupId)
				)}
			>
				{resetOptimization.isPending ? <Spinner /> : <RotateCcwIcon />}
				{t('general_revoke', { defaultValue: 'Revoke' })}
			</Button>
		)
	}

	return null
}
